// The points for the portfolio value chart on the Overview (added in v2 at Ben's request, 13 Oct 2026).
// 1D uses the hourly values from 24 hours ago; longer periods use the daily values (each day's
// close). Every point is the total value (holdings plus cash), converted to the display currency at
// that point's own USD/SGD rate, and the chart ends with the value shown in the hero right now.
/* eslint-disable @typescript-eslint/no-explicit-any */
import { isoOf, now } from './clock';
import { PERIODS, hourlyBase } from './periods';
import type { State } from './state';

export interface SeriesPoint { t: number; v: number; day?: string }
export interface Series { pts: SeriesPoint[]; daily: boolean; from: string | null }

const toDisplay = (S: State, sgd: number, fx: unknown): number => {
  if (S.display === 'SGD') return sgd;
  const r = Number(fx) > 0 ? Number(fx) : S.fx && S.fx.rate;
  return r ? sgd / r : sgd;
};
/** Noon on a calendar day, local time: a day's position on the time axis. */
const dayTime = (d: string) => { const [y, m, dd] = d.split('-').map(Number); return new Date(y, m - 1, dd, 12).getTime(); };

export function valueSeries(S: State, intraday: any[], per: string, liveValue: number | null): Series {
  const t0 = now();
  let pts: SeriesPoint[];
  const daily = per !== '1D';
  if (!daily) {
    // From the hourly value 1D is measured from (24 hours ago, or the earliest there is), so the line and
    // the change figure start at the same point.
    const seen = new Set<number>(), base = hourlyBase(intraday, t0), from = base ? base.t : t0;
    pts = (intraday || []).filter((p) => p && p.t >= from && p.t < t0 && Number(p.v) >= 0 && !seen.has(p.t) && seen.add(p.t))
      .sort((a, b) => a.t - b.t).map((p) => ({ t: p.t, v: toDisplay(S, Number(p.v), p.fx) }));
  } else {
    // From the daily value the hero's change is measured against (the last one on or before the start of
    // the period), so the line and the change figure cover the same stretch. Today's own daily value is
    // replaced by the live value.
    const start = isoOf(t0 - PERIODS[per][0] * 864e5), today = isoOf(t0);
    const H = (S.history || []).filter((h) => h.d < today && Number(h.sgd) >= 0);
    let i0 = 0; H.forEach((h, i) => { if (h.d <= start) i0 = i; });
    pts = H.slice(i0).map((h) => ({ t: dayTime(h.d), v: toDisplay(S, Number(h.sgd), h.fx), day: h.d }));
  }
  if (liveValue != null && isFinite(liveValue)) pts.push({ t: t0, v: liveValue });
  const first = daily ? (pts[0] && pts[0].day) || null : null;
  return { pts, daily, from: first };
}
